import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_BACKEND_URL is not set.");
}

export const API = `${BACKEND_URL}/api`;

const ACCESS_KEY = "vitae_access_token";
const REFRESH_KEY = "vitae_refresh_token";

export const tokenStore = {
  getAccess: () => {
    try { return localStorage.getItem(ACCESS_KEY) || null; } catch { return null; }
  },
  getRefresh: () => {
    try { return localStorage.getItem(REFRESH_KEY) || null; } catch { return null; }
  },
  set: (access, refresh) => {
    try {
      if (access) localStorage.setItem(ACCESS_KEY, access);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    } catch { /* ignore */ }
  },
  clear: () => {
    try {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    } catch { /* ignore */ }
  },
};

export const api = axios.create({
  baseURL: API,
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const t = tokenStore.getAccess();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Attempt a single silent refresh on 401 then retry original request.
let refreshInFlight = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (status !== 401 || original._retry) return Promise.reject(error);
    const rt = tokenStore.getRefresh();
    if (!rt) return Promise.reject(error);
    original._retry = true;
    try {
      if (!refreshInFlight) {
        refreshInFlight = axios.post(
          `${API}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${rt}` } }
        ).finally(() => { refreshInFlight = null; });
      }
      const res = await refreshInFlight;
      const newAccess = res?.data?.data?.access_token;
      if (newAccess) {
        tokenStore.set(newAccess, null);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      }
    } catch {
      tokenStore.clear();
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Merci de réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Format an axios error into a human-readable French message.
 * Distinguishes network errors (no response received) from API errors
 * (response with optional `detail` field).
 *
 * Backwards-compatible drop-in for sites that previously called
 * `formatApiErrorDetail(e.response?.data?.detail) || e.message`.
 */
export function formatApiError(err) {
  if (!err) return "Une erreur est survenue. Merci de réessayer.";
  // Network / CORS / DNS / mixed-content : pas de réponse HTTP du tout.
  if (!err.response) {
    const url = err.config?.baseURL || err.config?.url || API || "";
    const reason = err.message || "erreur réseau";
    return (
      `Impossible de joindre le serveur (${reason}). ` +
      `Vérifiez votre connexion ou la configuration de l'API` +
      (url ? ` (${url}).` : ".")
    );
  }
  // Réponse HTTP reçue : on privilégie le `detail` du backend.
  const detail = err.response?.data?.detail;
  if (detail != null) return formatApiErrorDetail(detail);
  // Fallback : code HTTP + status text
  const status = err.response.status;
  const statusText = err.response.statusText || "";
  if (status === 429) {
    return (
      "Trop de tentatives. Votre fournisseur (Cloudflare / WAF) a temporairement " +
      "bloqué cette requête. Patientez 1 à 2 minutes puis réessayez."
    );
  }
  if (status === 502 || status === 503 || status === 504) {
    return `Le serveur API est indisponible (${status}). Réessayez dans un instant.`;
  }
  if (status === 401) return "Identifiants invalides ou session expirée.";
  if (status === 403) return "Accès refusé.";
  if (status === 404) return "Ressource introuvable.";
  if (status >= 500) return `Erreur serveur (${status} ${statusText}).`;
  return `Erreur ${status} ${statusText}`.trim();
}
